/**
 * components/Maps/riderMarkerContent.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The rider pin, as a DOM node.
 *
 * This markup used to exist twice, once per tracking screen, wrapped in
 * L.divIcon. Google's AdvancedMarkerElement takes a DOM node directly, so the
 * same HTML now serves both screens through one builder.
 *
 * ANCHORING
 *   AdvancedMarkerElement puts the BOTTOM CENTRE of its content on the
 *   coordinate. The circular rider pin wants its CENTRE there (Leaflet
 *   iconAnchor [22,22]), so it is pushed down by half its height. The teardrop
 *   logo pin wants its tip there (iconAnchor [22,56]), which is already the
 *   default, so it is not offset.
 */

const COLORS = {
  green: "#10b981",
  yellow: "#f59e0b",
  gray: "#6b7280",
  white: "#ffffff",
};

const BIKE_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" fill="${COLORS.white}"><path d="M5 20.5A3.5 3.5 0 0 1 1.5 17A3.5 3.5 0 0 1 5 13.5A3.5 3.5 0 0 1 8.5 17A3.5 3.5 0 0 1 5 20.5M5 12a5 5 0 0 0-5 5a5 5 0 0 0 5 5a5 5 0 0 0 5-5a5 5 0 0 0-5-5m9.8-2H19v-2h-3.2l-2-3.4C13.4 4.2 12.8 4 12 4c-.8 0-1.3.3-1.7.8L7.5 8.3C7.2 8.7 7 9.1 7 9.5c0 .6.3 1 .8 1.3L11 12.7V17h2v-5.4L10 9.7l2.2-3.2M19 20.5A3.5 3.5 0 0 1 15.5 17A3.5 3.5 0 0 1 19 13.5A3.5 3.5 0 0 1 22.5 17A3.5 3.5 0 0 1 19 20.5M19 12a5 5 0 0 0-5 5a5 5 0 0 0 5 5a5 5 0 0 0 5-5a5 5 0 0 0-5-5z"/></svg>`;

/**
 * @param {object}  opts
 * @param {boolean} opts.isOnline
 * @param {boolean} opts.hasActiveOrder
 * @param {number?} opts.heading      degrees; rotates the pin when moving
 * @param {string?} opts.partnerLogo  renders the teardrop logo pin instead
 * @param {boolean} opts.isSelected   thicker ring so the chosen rider stands out
 * @returns {HTMLElement}
 */
export const buildRiderMarkerContent = ({
  isOnline,
  hasActiveOrder,
  heading = null,
  partnerLogo = null,
  isSelected = false,
} = {}) => {
  const accent = isOnline
    ? hasActiveOrder
      ? COLORS.yellow
      : COLORS.green
    : COLORS.gray;
  const ringColor = isOnline ? accent : "transparent";

  const wrapper = document.createElement("div");
  wrapper.className = "rider-marker";

  if (partnerLogo) {
    // Teardrop pin: tip sits on the coordinate, which is the default anchor.
    const clipId = `pinClip-${Math.random().toString(36).slice(2)}`;
    wrapper.innerHTML = `
      <div style="position:relative;width:44px;height:56px;">
        ${
          isOnline
            ? `<div class="rider-pulse-ring" style="position:absolute;top:4px;left:4px;width:36px;height:36px;border-radius:50%;border:2px solid ${ringColor};opacity:0.7;"></div>`
            : ""
        }
        <svg width="44" height="56" viewBox="0 0 44 56" style="position:absolute;top:0;left:0;${
          isOnline ? "" : "filter:grayscale(80%) opacity(0.7);"
        }">
          <defs>
            <clipPath id="${clipId}"><ellipse cx="22" cy="20" rx="17" ry="17"/></clipPath>
            <filter id="riderPinShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="3" stdDeviation="3" flood-opacity="0.35"/>
            </filter>
          </defs>
          <path d="M22 2C12.6 2 5 9.6 5 19c0 13.3 17 35 17 35s17-21.7 17-35C39 9.6 31.4 2 22 2z" fill="${accent}" filter="url(#riderPinShadow)"/>
          <image href="${partnerLogo}" x="5" y="3" width="34" height="34" clip-path="url(#${clipId})" preserveAspectRatio="xMidYMid slice"/>
          <path d="M22 2C12.6 2 5 9.6 5 19c0 13.3 17 35 17 35s17-21.7 17-35C39 9.6 31.4 2 22 2z" fill="none" stroke="${
            isSelected ? "#ffffff" : "rgba(255,255,255,0.5)"
          }" stroke-width="${isSelected ? 2.5 : 1.5}"/>
        </svg>
      </div>`;
    return wrapper;
  }

  // Circular pin: centre sits on the coordinate, so shift down by half.
  wrapper.style.transform = "translateY(50%)";
  const rotation =
    heading === null || heading === undefined
      ? ""
      : `transform:rotate(${heading}deg);`;

  wrapper.innerHTML = `
    <div style="position:relative;width:44px;height:44px;">
      ${
        isOnline
          ? `<div class="rider-pulse-ring" style="position:absolute;top:2px;left:2px;width:40px;height:40px;border-radius:50%;border:2.5px solid ${ringColor};"></div>`
          : ""
      }
      <div style="
        position:absolute;top:4px;left:4px;
        background:${accent};
        width:36px;height:36px;border-radius:50%;
        border:${isSelected ? "4px solid #023485" : `3px solid ${COLORS.white}`};
        box-shadow:0 3px 12px rgba(0,0,0,0.3);
        display:flex;align-items:center;justify-content:center;
        ${rotation}
      ">${BIKE_SVG}</div>
    </div>`;

  return wrapper;
};

export default buildRiderMarkerContent;
